// components/layout/Layout.tsx
import { ReactNode, useState, useEffect } from 'react';
import { Header } from './Header';
import Footer from './Footer';
import { projects } from '../../data/projects';

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Initialize from localStorage, defaulting to system preference
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) {
        return JSON.parse(saved);
      }
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  // Apply dark mode class and save preference
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      const hasPersistedPreference = localStorage.getItem('darkMode') !== null;
      if (!hasPersistedPreference) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const handleThemeToggle = () => {
    setIsDarkMode((prev: unknown) => !prev);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <Header
        projects={projects}
        onThemeToggle={handleThemeToggle}
        isDarkMode={isDarkMode}
      />
      <div className="flex-1 flex flex-col overflow-hidden">{children}</div>
      <Footer />
    </div>
  );
};

export default Layout;
